'use client';

import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import NextImage from 'next/image';
import { Flex, Skeleton } from '@skin-seoul/design-system';
import { SafeLink } from '@/components/ui/SafeLink';
import WishListButton from './components/WishListButton';
import HeaderComboboxSearchInput from './components/HeaderComboboxSearchInput/HeaderComboboxSearchInput';
import HeaderInput from './components/HeaderComboboxSearchInput/HeaderInput';
import { useCartDrawer } from '@skin-seoul/shared-components';
import { ClientOnly, useEventListener } from '@skin-seoul/utils';
import { ShoppingBagButton } from '@skin-seoul/shared-components';
import { FREE_SHIPPING_BANNER_HEIGHT } from '@/components/layout/Header/constants/css.client';
import { type Country } from '@skin-seoul/service';
import { overlay } from 'overlay-kit';
import { FiboSearchModal } from '@/components/fibosearch/FiboSearchModal';
import { CountryChangeButton } from '@skin-seoul/shared-components';
import ProfileButton from './components/ProfileButton';
const HeaderContentIconButton = ({
  ...rest
}: ComponentProps<typeof Flex>) => {
  return <Flex width={{
    base: 'auto'
  }} alignItems="center" justifyContent="center" {...rest} data-sentry-element="Flex" data-sentry-component="HeaderContentIconButton" data-sentry-source-file="HeaderContent.tsx" />;
};
type HeaderContentProps = {
  country: Country;
};
const HeaderContent = ({
  country
}: HeaderContentProps) => {
  const {
    onCartOpen
  } = useCartDrawer();
  const [isScrolled, setIsScrolled] = useState(false);
  const checkScrollPosition = useCallback(() => {
    if (window.scrollY > FREE_SHIPPING_BANNER_HEIGHT) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  }, []);
  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);
  useEventListener('scroll', checkScrollPosition);
  return <Flex as="nav" flexDirection="column" base={{
    height: 'fit-content',
    minHeight: '48px'
  }} desktop={{
    height: 'fit-content',
    minHeight: '60px',
    gap: '32px'
  }} width="100%" data-sentry-element="Flex" data-sentry-component="HeaderContent" data-sentry-source-file="HeaderContent.tsx">
      <Flex backgroundColor="black" boxShadow={isScrolled ? 'md' : 'none'} justifyContent="center" alignItems="center" left="0" paddingY={{
      base: '6px',
      tablet: 0,
      desktop: 0
    }} position={isScrolled ? 'fixed' : 'relative'} top="0" width="100%" zIndex={isScrolled ? 'docked' : 'auto'} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
        <Flex alignItems="center" justifyContent="center" maxWidth="1280px" width="100%" gap={{
        base: '16px',
        tablet: '32px'
      }} paddingX={{
        base: '16px',
        tablet: 'none'
      }} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
          <Flex alignItems="center" justifyContent={{
          base: 'center',
          tablet: 'flex-start'
        }} maxWidth={{
          base: '108px',
          tablet: '100%'
        }} width={{
          base: '15%',
          tablet: 'auto'
        }} paddingY="7px" data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
            <SafeLink href="/" data-sentry-element="SafeLink" data-sentry-source-file="HeaderContent.tsx">
              <Flex display={{
              base: 'block',
              tablet: 'none'
            }} flexShrink={0} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
                <NextImage alt="SkinSeoul" height={35} src="https://skin-seoul.com/wp-content/uploads/2025/01/skin-seoul-logo.png" width={32} data-sentry-element="NextImage" data-sentry-source-file="HeaderContent.tsx" />
              </Flex>
              <Flex display={{
              base: 'none',
              tablet: 'block'
            }} flexShrink={0} width="fit-content" data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
                <NextImage alt="SkinSeoul" height={90} src="https://skin-seoul.com/wp-content/uploads/2024/12/desktop-logo.png" width={250} data-sentry-element="NextImage" data-sentry-source-file="HeaderContent.tsx" />
              </Flex>
            </SafeLink>
          </Flex>
          <Flex width={{
          base: '100%'
        }} flexGrow={1} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
            <Flex width="100%" display={{
            base: 'none',
            tablet: 'block',
            desktop: 'block'
          }} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
              <HeaderComboboxSearchInput data-sentry-element="HeaderComboboxSearchInput" data-sentry-source-file="HeaderContent.tsx" />
            </Flex>
            <Flex onClick={() => {
            overlay.open(({
              isOpen,
              close,
              unmount
            }) => <FiboSearchModal isOpen={isOpen} onClose={close} onExit={unmount} />, {
              overlayId: 'search-modal'
            });
          }} width="100%" cursor="pointer" display={{
            base: 'block',
            tablet: 'none',
            desktop: 'none'
          }} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
              <HeaderInput readOnly userSelect="none" data-sentry-element="HeaderInput" data-sentry-source-file="HeaderContent.tsx" />
            </Flex>
          </Flex>
          <Flex alignItems="center" color="white" gap={{
          base: '16px',
          tablet: '14px'
        }} justifyContent={{
          base: 'center',
          tablet: 'space-between'
        }} width={{
          base: 'auto'
        }} flexShrink={0} data-sentry-element="Flex" data-sentry-source-file="HeaderContent.tsx">
            <HeaderContentIconButton display={{
            base: 'none',
            tablet: 'flex',
            desktop: 'flex'
          }} data-sentry-element="HeaderContentIconButton" data-sentry-source-file="HeaderContent.tsx">
              <SafeLink href="/wishlist/" data-sentry-element="SafeLink" data-sentry-source-file="HeaderContent.tsx">
                <WishListButton data-sentry-element="WishListButton" data-sentry-source-file="HeaderContent.tsx" />
              </SafeLink>
            </HeaderContentIconButton>
            <HeaderContentIconButton data-sentry-element="HeaderContentIconButton" data-sentry-source-file="HeaderContent.tsx">
              <ClientOnly fallback={<Skeleton width="24px" height="24px" borderRadius="50%" />} data-sentry-element="ClientOnly" data-sentry-source-file="HeaderContent.tsx">
                <CountryChangeButton country={country} data-sentry-element="CountryChangeButton" data-sentry-source-file="HeaderContent.tsx" />
              </ClientOnly>
            </HeaderContentIconButton>
            <HeaderContentIconButton display={{
            base: 'none',
            tablet: 'flex'
          }} data-sentry-element="HeaderContentIconButton" data-sentry-source-file="HeaderContent.tsx">
              <SafeLink href="/my-account/" data-sentry-element="SafeLink" data-sentry-source-file="HeaderContent.tsx">
                <ProfileButton data-sentry-element="ProfileButton" data-sentry-source-file="HeaderContent.tsx" />
              </SafeLink>
            </HeaderContentIconButton>
            <HeaderContentIconButton data-sentry-element="HeaderContentIconButton" data-sentry-source-file="HeaderContent.tsx">
              <ShoppingBagButton country={country} onClick={() => onCartOpen()} iconProps={{
              color: 'generic.0'
            }} data-sentry-element="ShoppingBagButton" data-sentry-source-file="HeaderContent.tsx" />
            </HeaderContentIconButton>
          </Flex>
        </Flex>
      </Flex>
    </Flex>;
};
export default HeaderContent;